import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import { ShieldCheck, Check, X, UserPlus, Crown, Clock, UsersThree, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};
const STATUS_CLASS = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-zinc-200 text-zinc-700",
};
const ROLE_LABEL = {
  admin: "Administrator",
  project_manager: "Project Manager",
  contractor: "Contractor",
};

export default function AdminPortalPage() {
  const { user, loading } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState("requests"); // 'requests' | 'users'
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/admin/signup-requests${params}`);
      setRequests(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  }, [filter]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_super_admin) load();
  }, [user, load]);

  useEffect(() => {
    if (user?.is_super_admin && section === "users") loadUsers();
  }, [user, section, loadUsers]);

  if (loading) return null;
  if (!user || !user.is_super_admin) return <Navigate to="/dashboard" replace />;

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/signup-requests/${id}/approve`);
      toast.success("Member created");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/signup-requests/${id}/reject`);
      toast.success("Request rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="space-y-10" data-testid="admin-portal">
        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="label-eyebrow text-[#007BFF]">Super-Admin</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#001E44] mt-2 flex items-center gap-3 flex-wrap">
              <Crown size={32} weight="fill" className="text-[#007BFF]" />
              Admin Portal
            </h1>
            <p className="text-zinc-500 mt-2 text-sm sm:text-base">
              Review incoming signup requests and create members directly. Only you see this page.
            </p>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                className="bg-[#007BFF] hover:bg-[#0062cc] rounded-full text-sm font-semibold px-5 py-2.5 h-auto shadow-sm hover:shadow-md transition-all"
                data-testid="create-member-button"
              >
                <UserPlus size={14} weight="bold" className="mr-2" /> Create member
              </Button>
            </DialogTrigger>
            <CreateMemberDialog onCreated={load} />
          </Dialog>
        </header>

        {/* Tile */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Tile icon={<Clock size={18} weight="bold" />} label="Pending review" value={pendingCount} accent="amber" />
          <Tile icon={<Check size={18} weight="bold" />} label="Approved" value={requests.filter((r) => r.status === "approved").length} accent="emerald" />
          <Tile icon={<ShieldCheck size={18} weight="bold" />} label="Total requests" value={requests.length} accent="primary" />
        </section>

        {/* Top-level section switch */}
        <Tabs value={section} onValueChange={setSection}>
          <TabsList>
            <TabsTrigger value="requests" data-testid="section-requests">Signup requests</TabsTrigger>
            <TabsTrigger value="users" data-testid="section-users">
              <UsersThree size={14} weight="bold" className="mr-1.5" /> All users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-6">
            {/* Requests */}
            <section className="space-y-4">
              <Tabs value={filter} onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger value="pending" data-testid="filter-pending">Pending ({pendingCount})</TabsTrigger>
              <TabsTrigger value="approved" data-testid="filter-approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected" data-testid="filter-rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
            </TabsList>
            <TabsContent value={filter} className="mt-4">
              {requests.length === 0 ? (
                <div className="ctrl-card p-12 text-center text-sm text-muted-foreground" data-testid="no-requests">
                  No {filter === "all" ? "" : filter} requests.
                </div>
              ) : (
                <div className="ctrl-card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="ctrl-table min-w-[760px]" data-testid="requests-table">
                      <thead>
                        <tr>
                          <th>Requester</th>
                          <th>Company</th>
                          <th>Role</th>
                          <th>Submitted</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((r) => (
                          <tr key={r.id} data-testid={`request-row-${r.id}`}>
                            <td>
                              <div className="font-semibold">{r.name}</div>
                              <div className="text-xs font-mono text-muted-foreground">{r.email}</div>
                              {r.note && (
                                <div className="text-xs text-muted-foreground italic mt-1 max-w-[220px]">
                                  &ldquo;{r.note}&rdquo;
                                </div>
                              )}
                            </td>
                            <td className="text-sm">{r.company || "—"}</td>
                            <td><span className="chip bg-zinc-100">{ROLE_LABEL[r.role]}</span></td>
                            <td className="font-mono text-xs text-muted-foreground">
                              {new Date(r.created_at).toLocaleDateString()}
                            </td>
                            <td>
                              <span className={`chip ${STATUS_CLASS[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                            </td>
                            <td className="text-right">
                              {r.status === "pending" && (
                                <div className="inline-flex items-center gap-2">
                                  <button
                                    onClick={() => approve(r.id)}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                    data-testid={`approve-request-${r.id}`}
                                  >
                                    <Check size={11} weight="bold" /> Approve
                                  </button>
                                  <button
                                    onClick={() => reject(r.id)}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-wider bg-zinc-200 text-zinc-700 hover:bg-zinc-300 transition-colors disabled:opacity-50"
                                    data-testid={`reject-request-${r.id}`}
                                  >
                                    <X size={11} weight="bold" /> Reject
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
            </section>
          </TabsContent>

          <TabsContent value="users" className="mt-6" data-testid="users-section">
            <section className="space-y-4">
              {usersLoading ? (
                <div className="ctrl-card p-12 text-center text-sm text-zinc-500">Loading users…</div>
              ) : users.length === 0 ? (
                <div className="ctrl-card p-12 text-center text-sm text-zinc-500">No users yet.</div>
              ) : (
                <div className="ctrl-card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="ctrl-table min-w-[760px]" data-testid="users-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Role</th>
                          <th>Company</th>
                          <th>Projects</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} data-testid={`user-row-${u.id}`}>
                            <td>
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-gradient-to-br from-[#001E44] to-[#1E407C] text-white rounded-full flex items-center justify-center text-xs font-semibold shrink-0">
                                  {u.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div>
                                  <div className="font-semibold text-[#001E44] flex items-center gap-1.5">
                                    {u.name}
                                    {u.is_super_admin && <Crown size={12} weight="fill" className="text-[#007BFF]" />}
                                  </div>
                                  <div className="text-xs font-mono text-zinc-500">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td><span className="chip bg-zinc-100">{ROLE_LABEL[u.role] || u.role}</span></td>
                            <td className="text-sm">{u.company || "—"}</td>
                            <td>
                              {u.project_count === 0 ? (
                                <span className="text-xs text-zinc-400">No projects yet</span>
                              ) : (
                                <div className="flex flex-wrap gap-1.5 max-w-[340px]" data-testid={`user-projects-${u.id}`}>
                                  {u.projects.map((p) => (
                                    <span
                                      key={p.id}
                                      className={`chip ${p.role === "owner" ? "bg-[#007BFF]/10 text-[#007BFF]" : p.role === "member" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-700"}`}
                                      title={`${p.role}${p.contractor_name ? ` · ${p.contractor_name}` : ""}`}
                                    >
                                      {p.name}
                                      <span className="ml-1 text-[9px] uppercase tracking-wider opacity-70">
                                        {p.role}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="text-right">
                              {!u.is_super_admin && u.id !== user.id && (
                                <DeleteUserButton userRow={u} onDeleted={loadUsers} />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function Tile({ icon, label, value, accent }) {
  const map = {
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    primary: "bg-[#007BFF]/10 text-[#007BFF]",
  };
  return (
    <div className="ctrl-card p-6">
      <div className="flex items-start justify-between mb-4">
        <span className="text-xs font-medium text-zinc-500">{label}</span>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${map[accent]}`}>
          {icon}
        </div>
      </div>
      <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#001E44]">{value}</div>
    </div>
  );
}

function DeleteUserButton({ userRow, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const ownedCount = (userRow.projects || []).filter((p) => p.role === "owner").length;
  const memberCount = (userRow.projects || []).filter((p) => p.role === "member").length;
  const contractorCount = (userRow.projects || []).filter((p) => p.role === "contractor").length;
  const canConfirm = confirmEmail.trim().toLowerCase() === (userRow.email || "").toLowerCase();

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${userRow.id}`);
      toast.success(`Deleted ${userRow.email}`);
      setOpen(false);
      setConfirmEmail("");
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirmEmail(""); }}>
      <DialogTrigger asChild>
        <button
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-wider bg-zinc-100 text-zinc-600 hover:bg-red-50 hover:text-red-700 transition-colors"
          data-testid={`delete-user-${userRow.id}`}
          title="Delete user"
        >
          <Trash size={11} weight="bold" /> Delete
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`delete-user-dialog-${userRow.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight text-[#001E44] flex items-center gap-2">
            <Warning size={20} weight="fill" className="text-red-600" />
            Delete user account
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-xl bg-red-50 border border-red-100 px-3 py-2.5 text-red-800">
            You&apos;re about to permanently delete <span className="font-semibold">{userRow.name}</span> ({userRow.email}). This cannot be undone.
          </div>
          <div className="space-y-1.5 text-zinc-700">
            <p className="font-medium text-[#001E44]">What happens next:</p>
            <ul className="list-disc list-inside space-y-1 text-xs leading-relaxed pl-1">
              {ownedCount > 0 && (
                <li><span className="font-semibold">{ownedCount}</span> project{ownedCount > 1 ? "s they own" : " they own"} will be transferred to your super-admin account.</li>
              )}
              {memberCount > 0 && (
                <li>They&apos;ll be removed as a member from <span className="font-semibold">{memberCount}</span> project{memberCount > 1 ? "s" : ""}.</li>
              )}
              {contractorCount > 0 && (
                <li><span className="font-semibold">{contractorCount}</span> contractor account{contractorCount > 1 ? "s" : ""} will be detached (procurement data kept, can be re-invited).</li>
              )}
              <li>Pending invites &amp; password reset links from this user will be revoked.</li>
            </ul>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Type the email to confirm</Label>
            <Input
              autoComplete="off"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={userRow.email}
              data-testid={`delete-user-confirm-input-${userRow.id}`}
            />
          </div>
        </div>
        <DialogFooter className="pt-2 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            data-testid={`delete-user-cancel-${userRow.id}`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canConfirm || busy}
            onClick={submit}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full text-xs font-semibold px-5 disabled:opacity-40"
            data-testid={`delete-user-confirm-${userRow.id}`}
          >
            <Trash size={12} weight="bold" className="mr-1.5" />
            {busy ? "Deleting…" : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateMemberDialog({ onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: "project_manager", company: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/members", form);
      toast.success(`Member created — welcome email sent to ${form.email}`);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md" data-testid="create-member-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold tracking-tight">Create member</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Name</Label>
          <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="cm-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Email</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="cm-email" />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Company</Label>
          <Input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} data-testid="cm-company" />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
            <SelectTrigger data-testid="cm-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="project_manager">Project Manager</SelectItem>
              <SelectItem value="contractor">Contractor</SelectItem>
              <SelectItem value="admin">Administrator</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Temporary password</Label>
          <Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="cm-password" />
          <p className="text-[11px] text-zinc-500 leading-snug">
            We&apos;ll email this password to <span className="font-medium text-zinc-700">{form.email || "the new member"}</span> along with the sign-in link. Share carefully.
          </p>
        </div>
        <DialogFooter className="pt-2">
          <Button type="submit" disabled={busy} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90" data-testid="cm-submit">
            {busy ? "Creating..." : "Create member"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
